import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Egg, Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { ForgotPassword } from "@/components/ForgotPassword";

const HERO =
  "https://images.unsplash.com/photo-1763517174756-cc27f6f63681?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxwb3VsdHJ5JTIwZmFybSUyMHN1bnJpc2V8ZW58MHx8fHwxNzg0NTQ3MzUxfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [step, setStep] = useState("creds"); // creds | mfa | forgot
  const [challengeId, setChallengeId] = useState("");
  const [phoneHint, setPhoneHint] = useState("");
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res.mfaRequired) {
        setChallengeId(res.challengeId);
        setPhoneHint(res.phoneHint);
        setStep("mfa");
      } else {
        navigate("/");
      }
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyMfa(challengeId, code);
      navigate("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="Poultry farm at sunrise" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#2B2A28]/55" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="uppercase text-xs tracking-[0.3em] mb-4 opacity-80">Montek Co &amp; PL</p>
          <h2 className="font-head text-4xl font-800 leading-none tracking-tight">
            Shift alerts,<br />sent in seconds.
          </h2>
          <p className="mt-4 text-white/80 max-w-md leading-relaxed">
            Roster your casual crew, then push SMS reminders to every available staff member with one tap.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        {step === "forgot" ? (
          <ForgotPassword initialEmail={email} onBack={() => { setStep("creds"); setError(""); setPassword(""); }} />
        ) : step === "creds" ? (
          <form onSubmit={submit} className="w-full max-w-sm animate-fade-up" data-testid="login-form">
            <div className="flex items-center gap-2 mb-8">
              <span className="h-10 w-10 rounded-md bg-primary flex items-center justify-center text-primary-foreground">
                <Egg className="h-5 w-5" />
              </span>
              <span className="font-head text-xl font-700 tracking-tight">Montek Co &amp; PL</span>
            </div>
            <h1 className="font-head text-3xl font-700 tracking-tight mb-1">Sign in</h1>
            <p className="text-muted-foreground text-sm mb-8">Manage staff shifts and SMS alerts.</p>

            <div className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input" className="mt-1.5" required />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Password</Label>
                  <button type="button" onClick={() => { setStep("forgot"); setError(""); }} data-testid="forgot-password-link" className="text-xs text-primary hover:underline">Forgot password?</button>
                </div>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input" className="mt-1.5" placeholder="••••••••" required />
              </div>
              {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
              <Button type="submit" disabled={loading} data-testid="login-submit-button"
                className="w-full transition-transform duration-200 hover:-translate-y-0.5">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                By signing in you agree to the <Link to="/terms" data-testid="login-terms-link" className="underline hover:text-foreground">Terms &amp; Conditions</Link>.
              </p>
            </div>
          </form>
        ) : (
          <form onSubmit={submitMfa} className="w-full max-w-sm animate-fade-up" data-testid="mfa-form">
            <div className="flex items-center gap-2 mb-8">
              <span className="h-10 w-10 rounded-md bg-primary flex items-center justify-center text-primary-foreground">
                <ShieldCheck className="h-5 w-5" />
              </span>
              <span className="font-head text-xl font-700 tracking-tight">Two-factor</span>
            </div>
            <h1 className="font-head text-3xl font-700 tracking-tight mb-1">Enter your code</h1>
            <p className="text-muted-foreground text-sm mb-8">
              We texted a 6-digit code to {phoneHint || "your phone"}.
            </p>
            <div className="space-y-4">
              <div>
                <Label htmlFor="code" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Verification code</Label>
                <Input id="code" inputMode="numeric" maxLength={6} value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  data-testid="mfa-code-input" className="mt-1.5 tracking-[0.5em] text-center text-lg" placeholder="000000" required />
              </div>
              {error && <p className="text-sm text-destructive" data-testid="mfa-error">{error}</p>}
              <Button type="submit" disabled={loading || code.length < 6} data-testid="mfa-submit-button" className="w-full">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & sign in"}
              </Button>
              <button type="button" onClick={() => { setStep("creds"); setCode(""); setError(""); }}
                data-testid="mfa-back-button" className="w-full inline-flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
